import { InputHTMLAttributes, forwardRef, ReactNode } from 'react';
import { X } from 'lucide-react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Pokaż przycisk X kasujący wartość (wymaga value + onChange controlled). */
  clearable?: boolean;
  onClear?: () => void;
}

const BASE =
  'w-full rounded-input bg-surface-base/60 text-text-primary text-sm ' +
  'border transition-colors duration-fast ease-smooth ' +
  'placeholder:text-text-disabled ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 focus-visible:border-accent-primary/50 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const STATE_DEFAULT = 'border-border';
const STATE_ERROR   = 'border-danger/60 focus-visible:ring-danger/30 focus-visible:border-danger';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { error, leftIcon, rightIcon, clearable, onClear, className, disabled, value, ...rest },
  ref,
) {
  const state = error ? STATE_ERROR : STATE_DEFAULT;
  const hasLeft = Boolean(leftIcon);
  const showClear = clearable && !!value && !disabled;
  const hasRight = Boolean(rightIcon) || showClear;

  const padLeft  = hasLeft  ? 'pl-9' : 'pl-field-x';
  const padRight = hasRight ? 'pr-9' : 'pr-field-x';

  return (
    <div className="relative">
      {hasLeft && (
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-4 h-4 text-text-muted [&>svg]:w-full [&>svg]:h-full pointer-events-none"
          aria-hidden="true"
        >
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        value={value}
        disabled={disabled}
        className={[BASE, state, `${padLeft} ${padRight} py-field-y`, className].filter(Boolean).join(' ')}
        {...rest}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-full text-text-muted hover:text-text-primary hover:bg-surface-hover transition-colors"
          aria-label="Wyczyść"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {!showClear && rightIcon && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-4 h-4 text-text-muted [&>svg]:w-full [&>svg]:h-full pointer-events-none"
          aria-hidden="true"
        >
          {rightIcon}
        </span>
      )}
    </div>
  );
});
